import { FC, useEffect, useState } from "react";
import { ExportBillingData, LineItemDto } from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { Box, Grid } from "@mui/material";
import BillBoxWrapper from "../../../DirectBilling/common/components/BillBoxWrapper";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import CorrectionBillEditBillInfo from "../../../CorrectionBilling/Edit/components/CorrectionBillEditBillInfo";
import LineItemSelector from "../../New/components/sub-components/LineItemSelector";
import { BillingTypeEnum } from "../../../Base/types/insuranceBills.types";
import { TestType } from "../../../DirectBilling/Edit/types/TestTypes.interface";
import { useLineItemCorrectionEdit } from "../hooks/useLineItemCorrectionEdit";

interface EditLineItemCorrectionBillFullScreenProps {
  handleFullScreenDialogClose: (skipWarning?: boolean) => void;
  initialBills: ExportBillingData[];
}

const EditLineItemCorrectionBillFullScreen: FC<
  EditLineItemCorrectionBillFullScreenProps
> = ({ handleFullScreenDialogClose, initialBills }) => {
  const [bills, setBills] = useState<ExportBillingData[]>(initialBills);
  const [selectedRx, setSelectedRx] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Track line item selections for each Rx
  const [lineItemSelections, setLineItemSelections] = useState<
    Record<string, LineItemDto[]>
  >({});

  const TransactionStore = useStore(TransactionsContext);
  const {
    refreshBillAmounts,
    getBillingDataForEditing,
    updateSelectedLineItems,
  } = TransactionStore;
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Initialize lineItemSelections from initialBills
  useEffect(() => {
    const selections: Record<string, LineItemDto[]> = {};
    initialBills.forEach((bill) => {
      bill.data.rxs.forEach((rx) => {
        if (!rx.id) return;
        // Cast to LineItemDto[] since the generated client has incorrect types
        if (rx.selectedLineItems && rx.selectedLineItems.length > 0) {
          selections[rx.id] = rx.selectedLineItems as any as LineItemDto[];
        } else if (rx.allLineItems && rx.allLineItems.length > 0) {
          // Default to all items if no selection exists
          selections[rx.id] = rx.allLineItems as any as LineItemDto[];
        }
      });
    });
    setLineItemSelections(selections);
  }, [initialBills]);

  const handleRxDelete = (deletedRxId: string) => {
    if (selectedRx && selectedRx.id === deletedRxId) {
      setSelectedRx(null);
      setIsVisible(false);
    }
    // Clean up line item selection
    setLineItemSelections((prev) => {
      const next = { ...prev };
      delete next[deletedRxId];
      return next;
    });
  };

  const handleRemoveAll = (billId: string) => {
    const targetBill = bills.find((b) => b.billId === billId);
    if (!targetBill) return;

    if (
      selectedRx &&
      targetBill.data.rxs.some((rx) => rx.id === selectedRx.id)
    ) {
      setSelectedRx(null);
      setIsVisible(false);
    }

    setBills((prevBills) => prevBills.filter((b) => b.billId !== billId));
  };

  const handleValidationFilter = (remainingRxs: any[]) => {
    if (selectedRx && !remainingRxs.some((rx) => rx.id === selectedRx.id)) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  const handleRowClick = (rx: any) => {
    const bill = bills.find((b) => b.data.rxs.some((r) => r.id === rx.id));

    if (!bill) {
      notificationStore.showMessage(
        "Zu diesem Rezept wurde keine Rechnung gefunden",
        "error",
      );
      return;
    }

    const selectedRxWithAccount = { ...rx, account: bill.accountInfo };
    setSelectedRx(selectedRxWithAccount);
    setIsVisible(true);
  };

  const handleLineItemSelectionChange = async (
    rxId: string,
    selectedItems: LineItemDto[],
  ) => {
    // Update local state
    setLineItemSelections((prev) => ({
      ...prev,
      [rxId]: selectedItems,
    }));

    // Find the transaction ID for this Rx
    const bill = bills.find((b) => b.data.rxs.some((r) => r.id === rxId));
    if (!bill) return;

    try {
      // Call backend to update selected line items
      const result = await updateSelectedLineItems(bill.billId, rxId, {
        selectedLineItems: selectedItems as any,
      });

      // Update the bill amount in local state
      setBills((prev) =>
        prev.map((b) =>
          b.billId === bill.billId
            ? {
                ...b,
                data: {
                  ...b.data,
                  totalPrice: result.amount || b.data.totalPrice,
                },
              }
            : b,
        ),
      );

      notificationStore.showMessage(
        "Positionen erfolgreich aktualisiert",
        "success",
      );
    } catch (error) {
      console.error("Error updating line items:", error);
      notificationStore.showMessage(
        "Fehler beim Aktualisieren der Positionen",
        "error",
      );
    }
  };

  useEffect(() => {}, [bills]);

  // Initialize the new batch workflow hook for LINE_ITEM_CORRECTION
  const {
    handleDrucken,
    isDrucken,
    batchProgress,
  } = useLineItemCorrectionEdit({
    bills,
    setBills,
    lineItemSelections,
    onFullScreenDialogClose: handleFullScreenDialogClose,
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={1} sx={{ height: "100%", marginTop: "1px" }}>
        <BillBoxWrapper xs={5}>
          <TopLevelPaper className="scrollable" sx={{ paddingTop: 0 }}>
            <CorrectionBillEditBillInfo
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              onRowClick={handleRowClick}
              onRxDelete={handleRxDelete}
              bills={bills}
              setBills={setBills}
              onValidationFilter={handleValidationFilter}
              onRemoveAll={handleRemoveAll}
              handleDruckenOverride={handleDrucken}
              isDruckenOverride={isDrucken}
              batchProgress={batchProgress}
            />
          </TopLevelPaper>
        </BillBoxWrapper>
        {isVisible && selectedRx && (
          <BillBoxWrapper xs={7}>
            <TopLevelPaper className="scrollable rounded-container">
              <LineItemSelector
                rxId={selectedRx.id}
                rxData={selectedRx}
                selectedLineItems={lineItemSelections[selectedRx.id] || []}
                onSelectionChange={(items) =>
                  handleLineItemSelectionChange(selectedRx.id, items)
                }
              />
            </TopLevelPaper>
          </BillBoxWrapper>
        )}
      </Grid>
    </Box>
  );
};

export default EditLineItemCorrectionBillFullScreen;
